import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  FiMail,
  FiMapPin,
  FiMessageCircle,
  FiPhone
} from "react-icons/fi";
import Navbar from "../../Components/Navbar/Navbar";
import Footer from "../../Components/Footer/Footer";
import "./ContactPage.css";

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  company: "",
  botcheck: ""
};


const fadeUp = {
  hidden: {
    opacity: 0,
    y: 50
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

function ContactPage() {
  const pageRef = useRef(null);

  const [formData, setFormData] = useState(initialFormData);

  const [status, setStatus] = useState({
    type: "idle",
    message: ""
  });


  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value
    }));

    if (
      status.type === "error" ||
      status.type === "success"
    ) {
      setStatus({
        type: "idle",
        message: ""
      });
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      return "Please enter your name.";
    }

    if (!formData.email.trim()) {
      return "Please enter your email address.";
    }

    const emailPattern =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(formData.email)) {
      return "Please enter a valid email address.";
    }

    if (!formData.phone.trim()) {
      return "Please enter your phone number.";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (status.type === "sending") {
      return;
    }

    const validationError = validateForm();

    if (validationError) {
      setStatus({
        type: "error",
        message: validationError
      });

      return;
    }

    if (!process.env.REACT_APP_WEB3FORMS_ACCESS_KEY) {
      setStatus({
        type: "error",
        message:
          "Web3Forms access key is not configured."
      });

      return;
    }

    setStatus({
      type: "sending",
      message: ""
    });

    try {
      const response = await fetch(
        "https://api.web3forms.com/submit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({
            access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY,
            subject: `New ETOE Contact Enquiry — ${formData.name}`,
            from_name: "ETOE Productions Website",
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company:
              formData.company.trim() ||
              "Not provided",
            botcheck: formData.botcheck
          })
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            "Unable to send your enquiry."
        );
      }

      setFormData(initialFormData);

      setStatus({
        type: "success",
        message:
          "Thank you. Your enquiry has been sent successfully."
      });

      window.setTimeout(() => {
        setStatus({
          type: "idle",
          message: ""
        });
      }, 5000);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.message ||
          "Something went wrong. Please try again."
      });
    }
  };

  const isSending =
    status.type === "sending";

  const isSuccess =
    status.type === "success";

  return (
    <>
      <Navbar />

      <main
        ref={pageRef}
        className="contact-page"
      >
        <section className="contact-page-hero">

          <div className="contact-page-hero-glow" />

          <div className="contact-page-container">

            <motion.h1
              className="contact-page-hero-title"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              Contact Us
            </motion.h1>

            <motion.p
              className="contact-page-hero-description"
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              Have an idea, project or story worth
              creating? Tell us what you have in mind
              and let's turn your vision into something
              unforgettable.
            </motion.p>

          </div>

        </section>

       <section className="contact-page-main">
  <div className="contact-page-container">

    {/* =========================================
        CONTACT FORM
    ========================================= */}

    <motion.div
      className="contact-page-contact-block"
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
    >
      <div className="contact-page-form-intro">
        <span className="contact-page-eyebrow">
          CONTACT US
        </span>

        <h2>
          Form to
          <span>Get in Touch</span>
        </h2>
         <p>
          Have a project, campaign or creative idea in mind?
          Share the details with us and our team will get back
          to you to discuss what comes next.
        </p>

      
      </div>

      <form
        className="contact-page-new-form"
        onSubmit={handleSubmit}
        noValidate
      >
        <input
          type="checkbox"
          name="botcheck"
          checked={Boolean(formData.botcheck)}
          onChange={(event) =>
            setFormData((current) => ({
              ...current,
              botcheck: event.target.checked
                ? "checked"
                : ""
            }))
          }
          className="contact-page-botcheck"
          tabIndex="-1"
          autoComplete="off"
        />

        <div className="contact-page-form-row">
          <div className="contact-page-field">
            <input
              id="contact-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder=" "
              autoComplete="name"
              required
              disabled={isSending}
            />
            <label htmlFor="contact-name">
              Your Name
            </label>
          </div>

          <div className="contact-page-field">
            <input
              id="contact-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder=" "
              autoComplete="email"
              required
              disabled={isSending}
            />
            <label htmlFor="contact-email">
              Email Address
            </label>
          </div>
        </div>

        <div className="contact-page-form-row">
          <div className="contact-page-field">
            <input
              id="contact-phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder=" "
              autoComplete="tel"
              required
              disabled={isSending}
            />
            <label htmlFor="contact-phone">
              Phone Number
            </label>
          </div>

          <div className="contact-page-field">
            <input
              id="contact-company"
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder=" "
              autoComplete="organization"
              disabled={isSending}
            />
            <label htmlFor="contact-company">
              Company Name
              <span>Optional</span>
            </label>
          </div>
        </div>

        <div
          className={`contact-page-status ${
            status.type !== "idle"
              ? `contact-page-status-${status.type}`
              : ""
          }`}
          aria-live="polite"
        >
          {status.message}
        </div>

        <button
          type="submit"
          className={`contact-page-submit ${
            isSuccess
              ? "contact-page-submit-success"
              : ""
          }`}
          disabled={isSending || isSuccess}
        >
          <span>
            {isSending
              ? "SENDING ENQUIRY"
              : isSuccess
              ? "ENQUIRY SENT"
              : "SEND ENQUIRY"}
          </span>

          <span className="contact-page-submit-icon">
            {isSending
              ? "•••"
              : isSuccess
              ? "✓"
              : "→"}
          </span>
        </button>
      </form>
    </motion.div>

    {/* =========================================
        CONTACT INFORMATION
    ========================================= */}

    <motion.section
      className="contact-page-details-section"
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
    >
      <div className="contact-page-details-heading">
        <span className="contact-page-eyebrow">
          CONTACT INFORMATION
        </span>

        <h2>Connect with ETOE</h2>

        <p>
          Reach us directly through any of the channels below.
        </p>
      </div>

      <div className="contact-page-detail-cards">

        <div className="contact-page-detail-card">
          <a
            href="tel:+919143219696"
            className="contact-page-detail-item"
          >
            <div className="contact-page-detail-icon">
              <FiPhone />
            </div>

            <div>
              <span>PHONE</span>
              <strong>+91 91432 19696 | +97 15426 61926</strong>
            </div>
          </a>

          <div className="contact-page-detail-divider" />

          <a
            href="https://wa.me/9143219696"
            target="_blank"
            rel="noreferrer"
            className="contact-page-detail-item"
          >
            <div className="contact-page-detail-icon">
              <FiMessageCircle />
            </div>

            <div>
              <span>WHATSAPP</span>
              <strong>+91 91432 19696</strong>
            </div>
          </a>
        </div>

        <div className="contact-page-detail-card">
          <a
            href="mailto:hello@etoeproductions.com"
            className="contact-page-detail-item"
          >
            <div className="contact-page-detail-icon">
              <FiMail />
            </div>

            <div>
              <span>EMAIL</span>
              <strong>
                info@etoeproductions.com
              </strong>
            </div>
          </a>

          <div className="contact-page-detail-divider" />

          <div className="contact-page-detail-item">
            <div className="contact-page-detail-icon">
              <FiMapPin />
            </div>

            <div>
              <span>LOCATION</span>
              <strong>
                Business Bay, Dubai, UAE
              </strong>
            </div>
          </div>
        </div>

      </div>
    </motion.section>

    {/* =========================================
        MAP
    ========================================= */}

    <motion.section
      className="contact-page-map-section"
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
    >
      <iframe
        title="ETOE Productions Location"
        src="https://www.google.com/maps?q=Business%20Bay%20Dubai%20UAE&output=embed"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </motion.section>

  </div>
</section>

      </main>

      <Footer />
    </>
  );
}

export default ContactPage;